/**
 * Таблица коммутаторов выбранного объекта.
 *
 * Поддерживает сортировку по любой колонке, поиск и два фильтра:
 * только с замечаниями и скрыть обработанные.
 */
import { useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { selectGrouped, selectPrefixMap } from '../../store/slices/switchesSlice';
import { setProcessed } from '../../store/slices/collabSlice';
import { selectCanEditObject, selectCanOpenNetbox } from '../../store/slices/authSlice';
import { objectLabel } from '../../domain/issues';
import { printObjectPassport, printSwitchLabels } from '../print/printing';
import { buildObjectTicket } from '../../domain/tickets';
import TicketModal from '../tickets/TicketModal';
import DivisionPicker from './DivisionPicker';
import NetboxExport from './NetboxExport';
import { plural } from '../../domain/plural';
import Icon from '../../components/common/Icon';
import ObjectTitle from '../../components/common/ObjectTitle';
import ui from '../../components/common/ui.module.css';
import styles from './SwitchTable.module.css';


/** Замечания отдельными метками — так их легче пробежать глазами. */
function Issues({ text, cleanText = 'нет' }) {
  const items = String(text || '').split(/;\s*/).filter(Boolean);
  if (!items.length) return <span className={styles.clean}>{cleanText}</span>;
  return (
    <span className={styles.issues}>
      {items.map((t) => <span key={t} className={styles.issue}>{t}</span>)}
    </span>
  );
}

/** Колонки в том же порядке, что и в прежней версии.
 *  secondary — скрывается на узких экранах. */
const COLUMNS = [
  { key: 'Шкаф',            label: 'Шкаф' },
  { key: 'Коммутатор',      label: 'Коммутатор', mono: true },
  { key: 'IP Коммутатора',  label: 'IP-адрес', mono: true },
  { key: 'Расположение',    label: 'Расположение' },
  { key: 'UP',              label: 'UP', secondary: true },
  { key: 'DOWN',            label: 'DOWN', secondary: true },
  { key: 'SN Netbox',       label: 'SN Netbox', mono: true, secondary: true },
  { key: 'SN Lyra',         label: 'SN Lyra', mono: true, secondary: true },
  { key: 'Модель SW',       label: 'Модель SW', secondary: true },
  { key: 'Замечания',       label: 'Замечания' },
];

export default function SwitchTable({ mag, onOpenCard }) {
  const dispatch = useDispatch();
  const grouped = useSelector(selectGrouped);
  const prefixMap = useSelector(selectPrefixMap);
  const statuses = useSelector((state) => state.collab.statuses);
  const canEdit = useSelector((state) => selectCanEditObject(state, mag));
  const canNetbox = useSelector((state) => selectCanOpenNetbox(state, mag));

  const [search, setSearch] = useState('');
  const [issuesOnly, setIssuesOnly] = useState(false);
  const [hideDone, setHideDone] = useState(false);
  const [sort, setSort] = useState({ column: null, desc: false });
  const [ticket, setTicket] = useState(null);
  const [netboxOpen, setNetboxOpen] = useState(false);
  const [notice, setNotice] = useState(null);

  const rows = useMemo(() => {
    let result = grouped[mag] ?? [];

    if (issuesOnly) result = result.filter((r) => r._issues > 0);
    if (hideDone) result = result.filter((r) => !statuses[`${mag}__${r['Коммутатор']}`]);

    const query = search.trim().toLowerCase();
    if (query) {
      result = result.filter((row) =>
        Object.values(row).some((value) => String(value).toLowerCase().includes(query))
      );
    }

    if (sort.column) {
      /* Сравнение с учётом языка и чисел: без него «MAG134_9» оказался бы
         после «MAG134_10», а порядок букв в русских названиях был бы
         неверным. */
      result = [...result].sort((a, b) => {
        const diff = String(a[sort.column] || '').localeCompare(
          String(b[sort.column] || ''), 'ru', { numeric: true }
        );
        return sort.desc ? -diff : diff;
      });
    }

    return result;
  }, [grouped, mag, issuesOnly, hideDone, statuses, search, sort]);

  function handleSort(column) {
    setSort((prev) =>
      prev.column === column ? { column, desc: !prev.desc } : { column, desc: false }
    );
  }

  function handleToggleDone(event, row) {
    event.stopPropagation();
    const sw = row['Коммутатор'];
    const done = Boolean(statuses[`${mag}__${sw}`]);
    dispatch(setProcessed({ mag, sw, done: !done }));
  }

  const all = grouped[mag] ?? [];
  const withIssues = all.filter((r) => r._issues > 0);
  const doneCount = withIssues.filter((r) => statuses[`${mag}__${r['Коммутатор']}`]).length;
  const label = objectLabel(mag, prefixMap);

  function handleTicket() {
    const result = buildObjectTicket(rows, label);
    if (result) { setTicket(result); setNotice(null); }
    else setNotice('На этом объекте нет записей с замечаниями — заявка не требуется.');
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.heading}>
          <div className={styles.titleRow}>
            <ObjectTitle className={styles.title}>{label}</ObjectTitle>
            <DivisionPicker mag={mag} />
          </div>
          <p className={styles.summary}>
            {all.length} {plural(all.length, 'коммутатор', 'коммутатора', 'коммутаторов')}
            {withIssues.length > 0
              ? `, с замечаниями ${withIssues.length}, обработано ${doneCount}`
              : ', замечаний нет'}
          </p>
        </div>
        <div className={styles.actions}>
          {/* На телефоне — короткие подписи: три кнопки в один ряд */}
          <button className={ui.button} onClick={handleTicket}>
            <span className={styles.long}>Сформировать заявку</span>
            <span className={styles.short}>Заявка</span>
          </button>
          <button className={ui.button} onClick={() => printObjectPassport(label, rows)}>
            <span className={styles.long}>Паспорт объекта</span>
            <span className={styles.short}>Паспорт</span>
          </button>
          {/* Наклейки — на те коммутаторы, что сейчас в таблице: можно
              отобрать, например, только без наклеек после замены */}
          <button
            className={ui.button}
            onClick={() => printSwitchLabels(label, rows.map((r) => ({
              sw: r['Коммутатор'],
              place: r['Шкаф'] ? `Шкаф ${r['Шкаф']}` : '',
            })), (name) => `${window.location.origin}/objects/${encodeURIComponent(mag)}/${encodeURIComponent(name)}`)}
            title="Наклейки с кодом на коммутаторы из таблицы — лист А4, 3 × 7"
          >
            <span className={styles.long}>Наклейки</span>
            <span className={styles.short}>QR</span>
          </button>
          {canNetbox && (
            <button className={ui.button} onClick={() => setNetboxOpen(true)}>
              <Icon name="netbox" size={16} />
              <span className={styles.long}>Отправка в NetBox</span>
              <span className={styles.short}>NetBox</span>
            </button>
          )}
        </div>
      </header>

      <div className={styles.toolbar}>
        <div className={styles.filters} role="group" aria-label="Отбор записей">
          <button
            className={`${styles.chip} ${issuesOnly ? styles.chipOn : ''}`}
            onClick={() => setIssuesOnly((v) => !v)}
            aria-pressed={issuesOnly}
          >
            Только с замечаниями
          </button>
          <button
            className={`${styles.chip} ${hideDone ? styles.chipOn : ''}`}
            onClick={() => setHideDone((v) => !v)}
            aria-pressed={hideDone}
          >
            Скрыть обработанные
          </button>
        </div>
        <span className={styles.rowCount}>
          {rows.length === all.length ? `${rows.length} записей` : `${rows.length} из ${all.length}`}
        </span>
        <input
          className={`${ui.search} ${styles.search}`}
          type="search"
          placeholder="Найти на объекте"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Найти коммутатор на объекте"
        />
      </div>

      {notice && <div className={styles.notice}>{notice}</div>}

      <div className={styles.wrap}>
        {rows.length === 0 ? (
          <div className={styles.empty}>По этому отбору записей нет.</div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.checkCell} title="Обработано" aria-label="Обработано">✓</th>
                {COLUMNS.map((column) => (
                  <th
                    key={column.key}
                    className={column.secondary ? styles.colSecondary : ''}
                    aria-sort={sort.column === column.key ? (sort.desc ? 'descending' : 'ascending') : undefined}
                  >
                    <button className={styles.sortButton} onClick={() => handleSort(column.key)}>
                      {column.label}
                      <span className={styles.sortMark} aria-hidden="true">
                        {sort.column === column.key ? (sort.desc ? '↓' : '↑') : ''}
                      </span>
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const sw = row['Коммутатор'];
                const done = Boolean(statuses[`${mag}__${sw}`]);
                return (
                  <tr
                    key={sw}
                    className={`${row._issues > 0 ? styles.rowIssue : ''} ${done ? styles.rowDone : ''}`}
                    onClick={() => onOpenCard(sw)}
                    onKeyDown={(e) => { if (e.key === 'Enter') onOpenCard(sw); }}
                    tabIndex={0}
                  >
                    <td className={styles.checkCell} onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={done}
                        disabled={!canEdit}
                        onChange={(e) => handleToggleDone(e, row)}
                        title={canEdit ? 'Отметить обработанным' : 'Недоступно для изменения'}
                      />
                    </td>
                    {COLUMNS.map((column) => (
                      <td
                        key={column.key}
                        className={[
                          column.secondary ? styles.colSecondary : '',
                          column.mono ? styles.mono : '',
                        ].filter(Boolean).join(' ')}
                      >
                        {column.key === 'Замечания'
                          ? <Issues text={row[column.key]} />
                          : row[column.key] || <span className={styles.muted}>—</span>}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Телефон: вместо таблицы — карточки. Таблица с прокруткой вбок
          на узком экране обрезала расположение и замечания. */}
      {rows.length > 0 && (
        <ul className={styles.cards} aria-label="Коммутаторы объекта">
          {rows.map((row) => {
            const sw = row['Коммутатор'];
            const done = Boolean(statuses[`${mag}__${sw}`]);
            const state = row._issues > 0 ? (done ? styles.cardChecked : styles.cardOpen) : '';
            return (
              <li key={sw} className={`${styles.card} ${state}`}>
                <button type="button" className={styles.cardOpenButton} onClick={() => onOpenCard(sw)}>
                  <span className={styles.cardName}>{sw}</span>
                  {/* Чего не хватает — видно по меткам замечаний, здесь только то, что есть */}
                  {(row['IP Коммутатора'] || row['Шкаф']) && (
                    <span className={styles.cardMeta}>
                      {row['IP Коммутатора'] && <span className={styles.mono}>{row['IP Коммутатора']}</span>}
                      {row['Шкаф'] && <span className={styles.cardTag}>шкаф {row['Шкаф']}</span>}
                    </span>
                  )}
                  {row['Расположение'] && <span className={styles.cardPlace}>{row['Расположение']}</span>}
                  <span className={styles.cardIssues}>
                    <Issues text={row['Замечания']} cleanText="без замечаний" />
                  </span>
                </button>
                {canEdit ? (
                  <label className={styles.cardDone} title="Отметить обработанным">
                    <input
                      type="checkbox"
                      checked={done}
                      onChange={(e) => handleToggleDone(e, row)}
                      aria-label={`${sw}: обработано`}
                    />
                  </label>
                ) : done && (
                  <span className={styles.cardDoneMark}>обработано</span>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {ticket && <TicketModal ticket={ticket} onClose={() => setTicket(null)} />}
      {netboxOpen && (
        <NetboxExport mag={mag} objectLabel={objectLabel(mag, prefixMap)} onClose={() => setNetboxOpen(false)} />
      )}
    </div>
  );
}
